export type RestaurantDateRangeUtc = {
  date_from: string;
  date_to: string;
};

const DATE_KEY_RE = /^\d{4}-\d{2}-\d{2}$/;
const TIME_RE = /^(\d{2}):(\d{2})(?::(\d{2}))?$/;

const getPartsFormatter = (timezone: string) =>
  new Intl.DateTimeFormat("en-CA", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });

const getDateTimePartsFormatter = (timezone: string) =>
  new Intl.DateTimeFormat("en-CA", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });

function parseDateKey(dateKey: string): {
  year: number;
  month: number;
  day: number;
} {
  const [year, month, day] = dateKey.split("-").map((part) => Number(part));
  return { year, month, day };
}

function formatDateKeyFromUtcDate(date: Date, timezone: string): string {
  const parts = getPartsFormatter(timezone).formatToParts(date);
  const value = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value;
  const year = value("year");
  const month = value("month");
  const day = value("day");
  if (!year || !month || !day) {
    throw new Error(`Unable to format date key for timezone ${timezone}`);
  }
  return `${year}-${month}-${day}`;
}

/**
 * Returns the restaurant-local YYYY-MM-DD key for any Date/ISO/date-key input.
 */
export function getRestaurantDateKey(
  date: Date | string,
  timezone: string,
): string {
  if (typeof date === "string" && DATE_KEY_RE.test(date)) return date;
  const parsed = typeof date === "string" ? new Date(date) : date;
  if (Number.isNaN(parsed.getTime())) {
    throw new Error("Invalid date passed to getRestaurantDateKey");
  }
  return formatDateKeyFromUtcDate(parsed, timezone);
}

function getTimezoneOffsetMs(timezone: string, date: Date): number {
  const parts = getDateTimePartsFormatter(timezone).formatToParts(date);
  const value = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value;

  const year = Number(value("year"));
  const month = Number(value("month"));
  const day = Number(value("day"));
  const hour = Number(value("hour"));
  const minute = Number(value("minute"));
  const second = Number(value("second"));

  const asUtc = Date.UTC(year, month - 1, day, hour, minute, second);
  return asUtc - date.getTime();
}

function zonedLocalTimeToUtc(
  dateKey: string,
  timezone: string,
  time: {
    hour?: number;
    minute?: number;
    second?: number;
    millisecond?: number;
  } = {},
): Date {
  const { year, month, day } = parseDateKey(dateKey);
  const utcGuess = Date.UTC(
    year,
    month - 1,
    day,
    time.hour ?? 0,
    time.minute ?? 0,
    time.second ?? 0,
    time.millisecond ?? 0,
  );
  const firstPass =
    utcGuess - getTimezoneOffsetMs(timezone, new Date(utcGuess));
  const secondPass =
    utcGuess - getTimezoneOffsetMs(timezone, new Date(firstPass));
  return new Date(secondPass);
}

export function getRestaurantDateTimeUtc(
  dateKey: string,
  time: string,
  timezone: string,
): string {
  if (!DATE_KEY_RE.test(dateKey)) {
    throw new Error("Invalid date key passed to getRestaurantDateTimeUtc");
  }

  const match = time.match(TIME_RE);
  if (!match) {
    throw new Error("Invalid time passed to getRestaurantDateTimeUtc");
  }

  const hour = Number(match[1]);
  const minute = Number(match[2]);
  const second = match[3] ? Number(match[3]) : 0;
  if (hour > 23 || minute > 59 || second > 59) {
    throw new Error("Invalid time passed to getRestaurantDateTimeUtc");
  }

  return zonedLocalTimeToUtc(dateKey, timezone, {
    hour,
    minute,
    second,
  }).toISOString();
}

function addCalendarDays(dateKey: string, days: number): string {
  const { year, month, day } = parseDateKey(dateKey);
  const date = new Date(Date.UTC(year, month - 1, day + days));
  return date.toISOString().slice(0, 10);
}

export function getRestaurantDayRangeUtc(
  date: Date | string,
  timezone: string,
): RestaurantDateRangeUtc {
  const dateKey = getRestaurantDateKey(date, timezone);
  const start = zonedLocalTimeToUtc(dateKey, timezone);
  const nextStart = zonedLocalTimeToUtc(addCalendarDays(dateKey, 1), timezone);
  return {
    date_from: start.toISOString(),
    date_to: new Date(nextStart.getTime() - 1).toISOString(),
  };
}

export function getRestaurantWeekRangeUtc(
  date: Date | string,
  timezone: string,
): RestaurantDateRangeUtc {
  const dateKey = getRestaurantDateKey(date, timezone);
  const { year, month, day } = parseDateKey(dateKey);
  const calendarDate = new Date(Date.UTC(year, month - 1, day));
  const dayOfWeek = calendarDate.getUTCDay();
  const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
  const mondayKey = addCalendarDays(dateKey, mondayOffset);
  const nextMondayKey = addCalendarDays(mondayKey, 7);
  const start = zonedLocalTimeToUtc(mondayKey, timezone);
  const nextStart = zonedLocalTimeToUtc(nextMondayKey, timezone);
  return {
    date_from: start.toISOString(),
    date_to: new Date(nextStart.getTime() - 1).toISOString(),
  };
}

export function getRestaurantDateRangeUtc(
  date: Date | string,
  viewMode: "day" | "week",
  timezone: string,
): RestaurantDateRangeUtc {
  return viewMode === "week"
    ? getRestaurantWeekRangeUtc(date, timezone)
    : getRestaurantDayRangeUtc(date, timezone);
}

export function isSameRestaurantDay(
  a: Date | string,
  b: Date | string,
  timezone: string,
): boolean {
  return (
    getRestaurantDateKey(a, timezone) === getRestaurantDateKey(b, timezone)
  );
}
